import React, { useEffect, useState } from 'react';
import axios from 'axios';

export type SuggestedOption = {
  optionset_id: number;
  option_id: number;
  name: string;
  price: string | number;
};

export type SuggestedLine = {
  fooditem_id: number;
  quantity: number;
  name: string;
  price: string | number;
  restaurant_address_id: number;
  restaurant_name: string;
  dietaries?: string[];
  needs_choices?: boolean;
  missing_groups?: string[];
  replaces_fooditem_id?: number;
  options?: SuggestedOption[];
};

type ChatMessage = { id?: number; role: string; content: string };

type Proposal = {
  lines: SuggestedLine[];
  food_total?: string;
  gap?: string;
  minimum_met?: boolean;
  follow_up?: string;
  coverage?: { quantity?: number; vegetarian_quantity?: number; vegan_quantity?: number };
};

type DeliveryAddress = {
  id?: number;
  address_line: string;
  city: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
  time_zone: string;
  suite_no?: string;
  street_number?: string;
  street?: string;
};

export type CartSnapshot = {
  fooditemId: number;
  name: string;
  quantity: number;
  price: number;
  options?: { name: string }[];
};

type Props = {
  date: string;
  timeZone: string;
  address?: DeliveryAddress | null;
  cart: CartSnapshot[];
  budget?: number;
  onAddressSaved?: (id: number) => void;
  onAdd: (lines: SuggestedLine[]) => void;
  onOpenItem: (line: SuggestedLine) => void;
};

const SAMPLE = 'Help me place an order for 20 people where 5 are vegetarians and 2 are vegans';

function authHeaders() {
  try {
    return JSON.parse(localStorage.getItem('headers') || '{}');
  } catch {
    return {};
  }
}

function money(value: string | number | undefined) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '$0.00';
  return `$${amount.toFixed(2)}`;
}

function suggestedFollowUp(proposal: Proposal | null, messages: ChatMessage[]) {
  const fromProposal = proposal?.follow_up?.trim() || '';
  if (fromProposal) return fromProposal.slice(0, 180);
  const text = [...messages].reverse().find(message => message.role === 'assistant')?.content?.trim() || '';
  if (text && text.length <= 180 && !text.includes('\n')) return text;
  return '';
}

function isVegan(line: SuggestedLine) {
  return (line.dietaries || []).some(tag => /vegan/i.test(tag));
}

function isVegetarian(line: SuggestedLine) {
  return !isVegan(line) && (line.dietaries || []).some(tag => /vegetarian/i.test(tag));
}

export default function WeekdayOrderAssistant({ date, timeZone, address, cart, budget, onAddressSaved, onAdd, onOpenItem }: Props) {
  const [chatId, setChatId] = useState<number | undefined>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    setChatId(undefined);
    setMessages([]);
    setProposal(null);
    setError('');
    setNotice('');
    setDraft('');
  }, [date, address?.address_line]);

  const lines = proposal?.lines || [];
  const ready = lines.filter(line => !line.needs_choices);
  const quantity = lines.reduce((sum, line) => sum + line.quantity, 0);
  const veganQuantity = lines.reduce((sum, line) => sum + (isVegan(line) ? line.quantity : 0), 0);
  const vegetarianQuantity = lines.reduce((sum, line) => sum + (isVegetarian(line) ? line.quantity : 0), 0);
  const suggestionFood = lines.reduce((sum, line) => sum + (Number(line.price) || 0) * line.quantity, 0);
  const cartMeals = cart.reduce((sum, line) => sum + line.quantity, 0);
  const cartFood = cart.reduce((sum, line) => sum + line.price * line.quantity, 0);
  const combined = suggestionFood + cartFood;
  const gap = Math.max(199 - combined, 0);
  const started = messages.length > 0 || busy || Boolean(error) || Boolean(notice);
  const outgoing = draft.trim() || (!started && address ? SAMPLE : '');
  const placeholder = !address ? 'Choose a delivery address first' : (started ? 'Ask a follow-up' : SAMPLE);

  async function send(text: string) {
    const message = text.trim();
    if (!message || busy) return;
    if (!address) {
      setError('Choose a delivery address first.');
      return;
    }
    setDraft('');
    setError('');
    setNotice('');
    setBusy(true);
    setMessages(current => current.concat({ role: 'user', content: message }));
    try {
      const response = await axios.post('/weekday_combo_chats', {
        date,
        address_id: address.id,
        address: {
          address_line: address.address_line,
          city: address.city,
          state: address.state,
          zip: address.zip,
          latitude: address.latitude,
          longitude: address.longitude,
          time_zone: address.time_zone,
          suite_no: address.suite_no,
          street_number: address.street_number,
          street: address.street
        },
        time_zone: timeZone,
        message,
        id: chatId,
        budget,
        cart: cart.map(line => ({
          fooditem_id: line.fooditemId,
          quantity: line.quantity,
          name: line.name,
          price: line.price,
          options: (line.options || []).map(option => option.name).filter(Boolean)
        })),
        proposal_lines: lines.map(line => ({
          fooditem_id: line.fooditem_id,
          quantity: line.quantity,
          name: line.name,
          price: line.price,
          options: (line.options || []).map(option => option.name),
          replaces_fooditem_id: line.replaces_fooditem_id
        }))
      }, { headers: authHeaders() });
      if (response.data.address_id) onAddressSaved?.(response.data.address_id);
      setChatId(response.data.id);
      const nextMessages = response.data.messages || [];
      const nextProposal = response.data.proposal || null;
      setMessages(nextMessages);
      setProposal(nextProposal);
      const followUp = suggestedFollowUp(nextProposal, nextMessages);
      if (followUp) setDraft(followUp);
    } catch (err) {
      const data = (err as { response?: { status?: number; data?: { message?: string; errors?: string[] } } })?.response;
      const errors = data?.data?.errors;
      const apiMessage = data?.data?.message || (Array.isArray(errors) ? errors.join(' ') : '');
      const signedOut = data?.status === 401 ? 'Sign in as a company admin to use the assistant.' : '';
      setError(apiMessage || signedOut || 'The assistant could not answer. Try again.');
    } finally {
      setBusy(false);
    }
  }

  function dismiss(fooditemId: number) {
    setProposal(current => {
      if (!current) return current;
      return { ...current, lines: current.lines.filter(line => line.fooditem_id !== fooditemId) };
    });
  }

  function accept() {
    if (!ready.length) return;
    onAdd(ready);
    const held = lines.filter(line => line.needs_choices);
    if (held.length) {
      setProposal(current => (current ? { ...current, lines: held } : current));
      setNotice('Added the meals that already have their required choices. The rest stay here.');
    } else {
      setProposal(null);
      setNotice('Added to your cart. Ask for anything else.');
    }
  }

  const composer = (
    <form className="wo-ask-bar" onSubmit={event => { event.preventDefault(); send(outgoing); }}>
      <span className="wo-ask-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 3.5l1.6 4.4L18 9.5l-4.4 1.6L12 15.5l-1.6-4.4L6 9.5l4.4-1.6L12 3.5z" fill="currentColor" />
          <path d="M18.2 14.2l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7.7-1.9z" fill="currentColor" />
        </svg>
      </span>
      <label className="wo-ask-field">
        <span>AI Concierge</span>
        <span className="wo-ask-line">
          <input
            value={draft}
            placeholder={placeholder}
            aria-label="Message AI Concierge"
            disabled={!address || busy}
            onChange={event => setDraft(event.target.value)}
          />
          {!draft && <span className="wo-ask-ghost" aria-hidden="true">{placeholder}<i className="wo-ask-caret" /></span>}
        </span>
      </label>
      <button type="submit" disabled={!address || busy || !outgoing}>
        {busy && <i className="wo-ask-spinner" aria-hidden="true" />}
        {busy ? 'Looking…' : 'Ask'}
      </button>
    </form>
  );

  return (
    <section className="wo-ask" aria-label="AI Concierge">
      {!started && composer}
      {started && (
        <div className="wo-ask-result">
          {notice && <p className="wo-ask-success">{notice}</p>}
          {error && <p className="wo-error">{error}</p>}
          {lines.length > 0 && (
            <>
              <div className="wo-ask-rail">
                {lines.map(line => (
                  <article className="wo-ask-card" key={line.fooditem_id}>
                    <button type="button" className="wo-ask-x" aria-label={`Remove ${line.name}`} onClick={() => dismiss(line.fooditem_id)}>×</button>
                    <button type="button" className="wo-ask-open" onClick={() => onOpenItem(line)}>
                      <b>{line.quantity}×</b>
                      <div>
                        <strong>{line.name}</strong>
                        <small>{line.restaurant_name} · {money(line.price)}</small>
                        {line.replaces_fooditem_id ? <small className="wo-ask-options">Replaces {cart.find(item => item.fooditemId === line.replaces_fooditem_id)?.name || 'a cart item'}</small> : null}
                        {(line.options || []).length > 0 && <small className="wo-ask-options">{line.options?.map(option => option.name).join(' · ')}</small>}
                        <span>
                          {(line.dietaries || []).slice(0, 2).map(tag => <em key={tag}>{tag}</em>)}
                          {line.needs_choices && <em className="warn">{(line.missing_groups || []).length ? `Needs ${(line.missing_groups || []).join(', ')}` : 'Choose options'}</em>}
                        </span>
                      </div>
                    </button>
                  </article>
                ))}
              </div>
              <div className="wo-ask-foot">
                <button
                  type="button"
                  className="wo-ask-add"
                  disabled={!ready.length}
                  onClick={accept}
                >
                  {ready.length ? (ready.every(line => line.replaces_fooditem_id) ? 'Update cart' : 'Add suggestion to cart') : 'Choose options'}
                </button>
                <p>
                  <strong>{quantity} meals</strong>
                  {' · '}{vegetarianQuantity} vegetarian
                  {' · '}{veganQuantity} vegan
                  {' · '}{money(suggestionFood)}
                  {cartMeals > 0 && <>{' · '}cart already has {cartMeals}</>}
                  {' · '}{gap === 0 ? 'minimum met' : `${money(gap)} short of $199`}
                </p>
              </div>
            </>
          )}
          {busy && (
            <p className="wo-ask-loader" role="status">
              <i className="wo-ask-spinner" aria-hidden="true" />
              Looking at today's menu…
            </p>
          )}
          {composer}
        </div>
      )}
    </section>
  );
}
